import { ReactElement } from "react"
import { LocalizeContextProps } from "react-localize-redux"
import { IPatient, IInsurance } from "../../../constants/types"
import { InsuranceClaim as InsuranceClaimType } from "../InsuranceClaims/types"

export interface Bill{
    uuid?:string,
    id:number,
    documentNumber:number,
    uuidPatient:string,
    billItems:BillItem[],
    type:DocumentType,
    status:DocumentStatus,
    uuidDepartment:string | null,
    uuidPrescribingDoctor:string | null,
    totalGross:number,
    totalDiscount:number,
    totalPaid:number,
    totalTax?:number,
    totalNet?:number,
    createdAt:Date,
    updatedAt:Date,
    // SHER-434: set by the single-bill GET. When true this document already has a LIVE
    // (non-cancelled) derived document, so the "Convert" buttons are hidden — only one live
    // child per source is allowed, and re-converting would be rejected by the API (400).
    hasActiveRelatedChild?:boolean
}

export interface Billable{
    id:number,
    concept:string,
    amount:number,
    insurance?:string,
    type: number,
    quantity?:number,
    // SHER-460: the billable's category (from its ServiceInvestigation), sent by the API
    // so the add-bill autocomplete can offer category-filter pills. Null/absent for
    // billables with no ServiceInvestigation (free/pharmacy/derived billables).
    category?: string | null,
    // Ids of billables associated to this one (the relatedBillables M2M). SHER-342: an act
    // linked to a PERCENT_CHARGE template (amount = percent) spawns a derived line for it.
    relatedBillables?: number[],
}

export interface BillableCombo{
    id:number,
    code:string,
    name:string,
    billables:{id:number}[]
}

export interface BillItem{
    id?: number;
    billableId?: number;
    concept: string;
    type: number;
    quantity: number;
    amount: number;
    // SHER-342: the linked PERCENT_CHARGE template (concept + percent) reaches an act bill
    // item two ways — stamped on the add path (resolved from the catalogue's relatedBillables)
    // and from the API on load (BillItem.serialize sets it). reconcileAnaesthesiaItems reads it
    // to regenerate the derived "[act] - {template}" line. Derived/other lines carry null.
    percentTemplate?: { concept: string; percent: number } | null;
    // Ids of related billables, carried when an act is freshly added from the catalogue (used
    // by addCurrentItem to resolve the percentTemplate / auto-add fixed-price related items).
    relatedBillables?: number[];
    paid?: number | Date | null;
    used?: number | Date | null;
    additionalInfoId?: number | null;
    paymentMethodInvestigationId?: number | null;
    paymentData?: {
        bankName?: string;
        chequeNumber?: string;
    };
    order?: number;
    
    // Insurance claim information
    insuranceClaim?: {
        id: number;
        approvedAmount: number;
        patientTotalBill: number;
    } | null;
    insuranceClaimId?: number | null;
    
    // Patient bill information (consolidated in one object)
    patientBill?: {
        documentNumber: string;
        uuid: string;
        createdAt: Date;  // ← Replaces patientBillCreatedAt
    } | null;
    patientUUID?: string | null;

    taxRateId?: number | null;
    taxAmount?: number | null;

    createdAt?: Date;
    updatedAt?: Date;
    deletedAt?: Date;
}

export interface PaymentMethod{
    id:number,
    name:string,
    code:string,
    customName:string,
    paymentData?:{
        bankName?:string,
        chequeNumber?:string
    },
}

export interface BillItemTable{
    id:number,
    concept:ReactElement,
    type?:ReactElement,
    colSpan?:BillItemKeys,
    amount:ReactElement,
    delete : JSX.Element,
    used?: JSX.Element,
    paid?: JSX.Element
}

export enum BillItemKeys{
    concept = "concept",
    amount = "amount",
    type = "type",
    quantity = "quantity",
}

export interface TaxRate {
    id?: number;
    uuid?: string;
    name: string;
    percentage: number;
    isDefault?: boolean;
    active?: boolean;
}

export interface BillingInfo{
    id:number,
    address:string,
    currency:string,
    phone:string,
    email:string,
    city:string,
    logoBlob:string,
    billables:Billable[],
    hospitalName:string,
    params:BillingInfoParams,
    fiscalId?:string,
    taxLabel?:string,
}

export interface BillingInfoParams {
    permissionDiscount?:boolean,
    discounts?:boolean,
    insurances?:boolean,
    budgets?:boolean,
    taxEnabled?:boolean,
    verifactu?:boolean,
    qrEnabled?:boolean,
}

export enum DocumentType{
    SUMMARY = 0,
    BUDGET = 1,
    INVOICE = 2,
}

export enum DocumentStatus{
    DRAFT = 0,
    CLOSED = 1,
    // SHER-423: soft-cancel ("Annulée"). Mirrors the backend DocumentStatus.
    // Cancelled documents are excluded from active billing lists by the API.
    CANCELLED = 2,
}

export interface EditBillingInfoProps {
    callbackUpdate : (values:any) => void
    billingInfo:BillingInfo,
}

export interface EditBillablesProps {
    billables : Billable[],
    billingInfo:BillingInfo,
    uuidInvestigation : string,
    withDiscount : boolean,
    onBillablesCreated:(billItems:BillItem[]) =>void
}

export interface EditBillingProps extends LocalizeContextProps, Omit<EditBillablesProps, | 'onBillablesCreated' >, Omit<EditBillingInfoProps, 'callbackUpdate'> {
    onBillingInfoSuccesfullyUpdated : (type:BillItemModes) => void;
}

export interface BillingInfoServiceResponse {
    status:number, billingInfo:BillingInfo
}

export interface BillablesServiceResponse {
    status: number, billables: Billable[] 
}

export interface BillServiceResponse {
    status: number, bill:Bill
}

// New interfaces for updated bill endpoint
export interface PaymentRecord {
    id: number,
    amount: number,
    paymentMethodInvestigationId: number,
    createdByResearcherUuid?: string,
    paymentData?: {
        type?: string,
        note?: string,
        reference?: string,
        bankName?: string,
        chequeNumber?: string
    } | null,
    paidAt: string,
    createdAt: string
}

export interface BillUpdateResponse {
    status: number,
    bill: Bill & {
        paymentRecords: PaymentRecord[]
    },
    paymentRecord: PaymentRecord | null
}

export interface BillUpdateErrorResponse {
    status: number,
    errorMessage: string,
    errorCode: number
}

// Type for creating/sending payment data (subset of PaymentRecord)
export type CreatePaymentRequest = Pick<PaymentRecord, 'amount' | 'paymentMethodInvestigationId' | 'paymentData'>

export enum BillItemModes{
    SHOW = "SHOW",
    EDIT = "EDIT",
    // BILL = "bill",
    // BILLABLE = "billable",
    // GENERAL = "general"
}

// Insurance Bills Report Types
export interface Organization {
    id: number;
    name: string;
    uuid?: string;
}

export interface InsuranceBillsReportData {
    organization: Organization;
    bills: BillWithPEC[];
    startDate?: Date;
    endDate?: Date;
    totalApprovedAmount: number;
}

export interface BillWithPEC {
    bill: Bill;
    patient: IPatient;
    pec: InsuranceClaimType;
    approvedAmount: number;
}

// Insurance Bills Types
export interface InsuranceBill {
    uuid: string;
    organizationUuid: string;
    organizationName: string;
    totalGross: number;
    totalPaid: number;
    totalDiscount: number;
    status: DocumentStatus; // 0 = DRAFT, 1 = CLOSED
    type: DocumentType; // 2 = INVOICE
    billItems?: BillItem[];
    paymentRecords?: PaymentRecord[];
    coveragePercentage: number | null;
    insuranceCode: string;
    createdAt: string;
    updatedAt: string;
}

export interface InsuranceBillsResponse {
    status: number;
    bills: InsuranceBill[];
    errorMessage?: string;
}

export interface InsuranceBillResponse {
    status: number;
    bill?: InsuranceBill;
    errorMessage?: string;
}

// New comprehensive Insurance Claim interface
export interface IInsuranceClaim {
    // Core fields
    id?: number;
    uuid?: string;
    
    // Claim details
    organizationName?: string;
    coveragePercentage?: number;
    requestedAmount?: number;
    approvedAmount?: number;
    guaranteeLetterNumber?: string;
    guaranteeLetterIssueDate?: Date;
    interventionDate?: Date;
    amountInLetters?: string;
    guaranteeLetterPdfPath?: string;
    approvalLetterImageUrl?: string;
    status?: string;
    claimReference?: string;
    notes?: string;
    
    // Relationships
    billId?: number;
    organizationId?: number;
    organizationType?: string;
    organizationUuid?: string;
    coverageId?: number;
    createdByResearcherId?: number;
    updatedByResearcherId?: number;
    
    // Audit fields
    createdAt?: Date;
    updatedAt?: Date;
    
    // NEW: Patient payment tracking
    patientUuid?: string;           // Patient UUID from original bill
    patientPaymentAmount?: number;  // Amount paid by patient (from bill.totalPaid)
}

// Insurance Claim for Insurance Bills (simplified version from API)
// This interface is kept for backward compatibility with existing API responses
export interface InsuranceBillClaim {
    id: number;
    requestedAmount: number;
    approvedAmount?: number;
    coveragePercentage: number;
    status: string; // 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'PARTIALLY_APPROVED' | 'DENIED' | 'PAID'
    guaranteeLetterNumber: string;
    guaranteeLetterIssueDate: string;
    interventionDate: string;
    organization: {
        id: number;
        name: string;
        uuid: string;
    };
    bill: {
        uuid: string;
        billIdInvestigation: number;
    };
    createdAt: string;
    updatedAt: string;
    // Optional: Support for new fields
    patientUuid?: string;           // Patient UUID from original bill
    patientPaymentAmount?: number;  // Amount paid by patient
    patientBill?: {                 // Patient bill information (consolidated)
        documentNumber: string;
        uuid: string;
        createdAt: string;
    } | null;
}

export interface ClaimsResponse {
    status: number;
    insuranceClaims?: InsuranceBillClaim[];
    errorMessage?: string;
}
